<template>
    <transition name="modal-drop">
      <div v-if="project" class="fixed inset-0 z-50 overflow-y-auto" @click.self="$emit('close')">
        <div class="flex items-center justify-center min-h-screen px-4 py-8">
          <div class="fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity" @click="$emit('close')"></div>
          <div class="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden">
            <!-- Modal Header -->
            <div class="relative h-72 bg-slate-900 overflow-hidden">
              <img v-if="project.mediaType === 'image'" 
                   :src="project.media" 
                   :alt="project.title" 
                   @click.stop="$emit('view-image', project.media)"
                   class="w-full h-full object-contain cursor-zoom-in" />
              <div v-else-if="project.mediaType === 'video'" class="relative w-full h-full">
                <video :src="project.media" 
                       class="w-full h-full object-contain" 
                       controls></video>
                <div v-if="!isVideoPlaying" @click="playVideo" class="absolute inset-0 flex items-center justify-center bg-black/20 cursor-pointer">
                  <div class="w-20 h-20 bg-emerald-500/80 backdrop-blur-md rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-lg">
                    <svg class="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none"></div>
              <button @click="$emit('close')" 
                      class="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/20 transition-colors">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div class="absolute bottom-6 left-8 right-8">
                <h2 class="text-3xl font-bold text-white">{{ project.title }}</h2>
              </div>
            </div>
            
            <!-- Modal Content -->
            <div class="p-8">
              <p class="text-lg text-slate-600 mb-8 leading-relaxed">{{ project.fullDescription }}</p>
              
              <div class="mb-8">
                <h3 class="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Technologies</h3>
                <div class="flex flex-wrap gap-2">
                  <span v-for="tech in project.technologies" 
                        :key="tech" 
                        class="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium">
                    {{ tech }}
                  </span>
                </div>
              </div>

              <div v-if="project.features && project.features.length > 0" class="mb-8">
                <h3 class="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Key Features</h3>
                <ul class="space-y-3">
                  <li v-for="feature in project.features" :key="feature" class="flex items-start gap-3">
                    <svg class="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span class="text-slate-600">{{ feature }}</span>
                  </li>
                </ul>
              </div>

              <div v-if="project.challenges" class="mb-8">
                <h3 class="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Challenges & Solutions</h3>
                <p class="text-slate-600 leading-relaxed">{{ project.challenges }}</p>
              </div>

              <div class="flex flex-col sm:flex-row gap-4 pt-4 border-t border-slate-100">
                <a v-if="project.link" 
                   :href="project.link" 
                   target="_blank" 
                   class="flex-1 px-6 py-3.5 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-colors text-center">
                  Visit Live Site
                </a>
                <a v-if="project.githubLink" 
                   :href="project.githubLink" 
                   target="_blank" 
                   class="flex-1 px-6 py-3.5 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors text-center flex items-center justify-center gap-2">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  View on GitHub
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  project: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'view-image'])

const isVideoPlaying = ref(false)

const playVideo = (event) => {
  const video = event.target.closest('.relative').querySelector('video')
  if (video) {
    video.play()
    isVideoPlaying.value = true
  }
}

const handleEscape = (e) => {
  if (e.key === 'Escape' && props.project) {
    emit('close')
  }
}

// Watch for project changes to handle body scroll lock
watch(() => props.project, (newVal) => {
  if (newVal) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.body.style.overflow = ''
})
</script>
